import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { DeleteOutline, AddCircleOutline } from "@mui/icons-material";
import useLoader from "../../../../hooks/useLoader.js";
import useModal from "../../../../hooks/useModal.js";
import { createLwfMaster, updateLwfMaster, getLwfMasters, getZones } from "../../../../services/lwfMaster.js";

// Compliance Type is never shown to the user - this module only ever manages LWF records.
const COMPLIANCE_TYPE = "LWF";

const AMOUNT_TYPE_OPTIONS = [
  { label: "Fixed", value: "fixed" },
  { label: "Percentage", value: "percentage" },
];

const FREQUENCY_OPTIONS = [
  { label: "Monthly", value: 1 },
  { label: "Quarterly", value: 3 },
  { label: "Half-Yearly", value: 6 },
  { label: "Yearly", value: 12 },
];

const STATUS_OPTIONS = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];

const INITIAL_FORM = {
  state: "",
  salary_from: "",
  salary_to: "",
  amount_type: "fixed",
  employee_contribution: "",
  employee_contribution_max: "",
  employer_contribution: "",
  employer_contribution_max: "",
  frequency: 1,
  effective_from: "",
  effective_to: "",
  status: 1,
};

// Backend validation errors (zod) are keyed by the backend field name, which differs from
// the frontend form field name only for state (state_id -> state).
const SERVER_FIELD_MAP = { state_id: "state" };

const isBlank = (val) => val === "" || val === null || val === undefined;

// Same closed-interval overlap test the backend uses: existing.salary_from <= newTo AND
// existing.salary_to >= newFrom. Boundary values count as overlapping (0-1200 then 1200-1500
// is rejected; 1201-1500 is not), so gaps between ranges stay allowed without forcing a
// continuous "previous salary_to + 1" sequence.
const findOverlap = (from, to, candidates) => candidates.find((c) => c.salary_from <= to && c.salary_to >= from);

// Pure validation for a single row's fields. conflictRanges is the combined set of
// existing DB ranges (for the row's state, excluding the record itself on edit) plus every
// other row currently open in the same modal that shares that state.
function computeFieldErrors(formData, { isPercentage, conflictRanges }) {
  const errors = {};

  if (!formData.state) errors.state = "State is required";

  const fromBlank = isBlank(formData.salary_from);
  const toBlank = isBlank(formData.salary_to);
  const fromNum = Number(formData.salary_from);
  const toNum = Number(formData.salary_to);
  const fromValid = !fromBlank && !Number.isNaN(fromNum) && fromNum >= 0;
  const toValid = !toBlank && !Number.isNaN(toNum) && toNum >= 0;

  if (fromBlank) {
    errors.salary_from = "Salary From is required";
  } else if (Number.isNaN(fromNum)) {
    errors.salary_from = "Salary From must be a valid number";
  } else if (fromNum < 0) {
    errors.salary_from = "Salary From must be greater than or equal to 0";
  }

  // Salary To is optional: a blank value means the range is open-ended (no upper bound).
  if (!toBlank) {
    if (Number.isNaN(toNum)) {
      errors.salary_to = "Salary To must be a valid number";
    } else if (toNum < 0) {
      errors.salary_to = "Salary To must be greater than or equal to 0";
    }
  }

  // Overlap check runs off Salary From alone once it's valid, using Salary To when present or
  // treating the range as open-ended (up to infinity) when it's blank, so an in-range value is
  // flagged immediately instead of waiting for both fields to be filled.
  if (fromValid && !errors.salary_from) {
    if (toValid && !errors.salary_to && toNum <= fromNum) {
      errors.salary_to = "Salary To must be greater than Salary From";
    } else if (formData.state && conflictRanges && conflictRanges.length) {
      const effectiveTo = toValid && !errors.salary_to ? toNum : Infinity;
      const conflict = findOverlap(fromNum, effectiveTo, conflictRanges);
      if (conflict) {
        if (toValid && !errors.salary_to) {
          errors.salary_to = `Salary range overlaps with existing range ${conflict.salary_from}-${conflict.salary_to} for this state`;
        } else {
          errors.salary_from = `Salary From must be greater than ${conflict.salary_to} (overlaps with existing range ${conflict.salary_from}-${conflict.salary_to})`;
        }
      }
    }
  }

  if (!formData.amount_type) errors.amount_type = "Amount Type is required";

  if (!isBlank(formData.employee_contribution)) {
    if (Number.isNaN(Number(formData.employee_contribution))) {
      errors.employee_contribution = "Employee Contribution must be a valid number";
    } else if (Number(formData.employee_contribution) < 0) {
      errors.employee_contribution = "Employee Contribution must be greater than or equal to 0";
    } else if (isPercentage && Number(formData.employee_contribution) > 100) {
      errors.employee_contribution = "Employee Contribution must be between 0 and 100";
    }
  }

  if (isBlank(formData.employer_contribution)) {
    errors.employer_contribution = "Employer Contribution is required";
  } else if (Number.isNaN(Number(formData.employer_contribution))) {
    errors.employer_contribution = "Employer Contribution must be a valid number";
  } else if (Number(formData.employer_contribution) < 0) {
    errors.employer_contribution = "Employer Contribution must be greater than or equal to 0";
  } else if (isPercentage && Number(formData.employer_contribution) > 100) {
    errors.employer_contribution = "Employer Contribution must be between 0 and 100";
  }

  if (isPercentage) {
    if (isBlank(formData.employee_contribution_max)) {
      errors.employee_contribution_max = "Employee Contribution Max is required";
    } else if (Number.isNaN(Number(formData.employee_contribution_max)) || Number(formData.employee_contribution_max) < 0) {
      errors.employee_contribution_max = "Employee Contribution Max must be a valid number greater than or equal to 0";
    }

    if (isBlank(formData.employer_contribution_max)) {
      errors.employer_contribution_max = "Employer Contribution Max is required";
    } else if (Number.isNaN(Number(formData.employer_contribution_max)) || Number(formData.employer_contribution_max) < 0) {
      errors.employer_contribution_max = "Employer Contribution Max must be a valid number greater than or equal to 0";
    }
  }

  if (!formData.frequency) errors.frequency = "Frequency is required";

  if (formData.effective_from && Number.isNaN(new Date(formData.effective_from).getTime())) {
    errors.effective_from = "Effective From must be a valid date";
  }

  if (formData.effective_to) {
    if (Number.isNaN(new Date(formData.effective_to).getTime())) {
      errors.effective_to = "Effective To must be a valid date";
    } else if (formData.effective_from && new Date(formData.effective_to) < new Date(formData.effective_from)) {
      errors.effective_to = "Effective To must be on or after Effective From";
    }
  }

  return errors;
}

function getConflictRangesForRow(rowsSnapshot, index, rangesByState, excludeId) {
  const row = rowsSnapshot[index];
  const stateId = row.formData.state;
  if (!stateId) return [];
  const key = String(stateId);

  const dbRanges = (rangesByState[key]?.ranges || [])
    .filter((r) => !excludeId || r.id !== excludeId)
    .map((r) => ({ ...r, salary_to: isBlank(r.salary_to) || Number.isNaN(r.salary_to) ? Infinity : r.salary_to }));

  const siblingRanges = rowsSnapshot
    .filter((r, i) => i !== index && String(r.formData.state) === key)
    .map((r) => ({
      salary_from: Number(r.formData.salary_from),
      salary_to: isBlank(r.formData.salary_to) ? Infinity : Number(r.formData.salary_to),
    }))
    .filter((r) => !Number.isNaN(r.salary_from) && !Number.isNaN(r.salary_to));

  return [...dbRanges, ...siblingRanges];
}

function recomputeAllRows(rowsSnapshot, rangesByState, excludeId) {
  return rowsSnapshot.map((row, index) => {
    if (row.saved) return row;
    const isPercentage = row.formData.amount_type === "percentage";
    const conflictRanges = getConflictRangesForRow(rowsSnapshot, index, rangesByState, excludeId);
    const errors = computeFieldErrors(row.formData, { isPercentage, conflictRanges });
    return { ...row, errors };
  });
}

function extractServerFieldErrors(respData) {
  const issues = Array.isArray(respData?.data) ? respData.data : null;
  if (!issues || !issues.length) return {};

  const fieldErrors = {};
  issues.forEach((issue) => {
    const backendField = issue?.path?.[0];
    if (!backendField) return;
    const field = SERVER_FIELD_MAP[backendField] || backendField;
    fieldErrors[field] = issue.message;
  });
  return fieldErrors;
}

function buildPayload(formData, isPercentage) {
  const payload = {
    compliance_type: COMPLIANCE_TYPE,
    state: Number(formData.state),
    salary_from: Number(formData.salary_from),
    salary_to: isBlank(formData.salary_to) ? null : Number(formData.salary_to),
    amount_type: formData.amount_type,
    employee_contribution: isBlank(formData.employee_contribution) ? null : Number(formData.employee_contribution),
    employer_contribution: Number(formData.employer_contribution),
    frequency: Number(formData.frequency),
    status: Number(formData.status),
  };

  if (isPercentage) {
    payload.employee_contribution_max = Number(formData.employee_contribution_max);
    payload.employer_contribution_max = Number(formData.employer_contribution_max);
  }

  if (formData.effective_from) {
    payload.effective_from = formData.effective_from;
  }

  if (formData.effective_to) {
    payload.effective_to = formData.effective_to;
  }

  return payload;
}

const LwfRowForm = ({ row, index, zones, isEdit, canRemove, isSubmitting, onFieldChange, onAmountTypeChange, onRemove }) => {
  const { formData, errors, saved } = row;
  const isPercentage = formData.amount_type === "percentage";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "amount_type") {
      onAmountTypeChange(index, value);
    } else if (name === "status") {
      onFieldChange(index, name, Number(value));
    } else {
      onFieldChange(index, name, type === "checkbox" ? checked : value);
    }
  };

  const disabled = saved || isSubmitting;

  return (
    <div
      style={{
        backgroundColor: "#f9f9f9",
        padding: "14px",
        borderRadius: "6px",
        border: saved ? "1px solid #28a745" : "1px solid #e0e0e0",
        marginBottom: "14px",
      }}
    >
      {!isEdit && (
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong style={{ fontSize: "13px" }}>Row {index + 1}</strong>
          {canRemove && !saved && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              disabled={isSubmitting}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#dc3545", padding: 0 }}
              title="Remove row"
            >
              <DeleteOutline fontSize="small" />
            </button>
          )}
        </div>
      )}

      <div className="row">
        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            State <span className="text-danger">*</span>
          </label>
          <select
            className={`form-select form-select-sm ${errors.state ? "is-invalid" : ""}`}
            name="state"
            value={formData.state}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="">Select State</option>
            {zones.map((zone) => (
              <option key={zone._ZoneID} value={zone._ZoneID}>
                {zone._Name}
              </option>
            ))}
          </select>
          {errors.state && <div className="invalid-feedback">{errors.state}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Salary From <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            min="0"
            name="salary_from"
            className={`form-control form-control-sm ${errors.salary_from ? "is-invalid" : ""}`}
            value={formData.salary_from}
            onChange={handleChange}
            placeholder="Enter salary from"
            disabled={disabled}
          />
          {errors.salary_from && <div className="invalid-feedback">{errors.salary_from}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Salary To
          </label>
          <input
            type="number"
            min="0"
            name="salary_to"
            className={`form-control form-control-sm ${errors.salary_to ? "is-invalid" : ""}`}
            value={formData.salary_to}
            onChange={handleChange}
            placeholder="Enter salary to (optional)"
            disabled={disabled}
          />
          {errors.salary_to && <div className="invalid-feedback">{errors.salary_to}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Amount Type <span className="text-danger">*</span>
          </label>
          <select className="form-select form-select-sm" name="amount_type" value={formData.amount_type} onChange={handleChange} disabled={disabled}>
            {AMOUNT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.amount_type && <div className="invalid-feedback d-block">{errors.amount_type}</div>}
        </div>

        {isPercentage ? (
          <>
            <div className="col-md-4 mb-3">
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                Employee Contribution (In %)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                name="employee_contribution"
                className={`form-control form-control-sm ${errors.employee_contribution ? "is-invalid" : ""}`}
                value={formData.employee_contribution}
                onChange={handleChange}
                placeholder="e.g. 0.75"
                disabled={disabled}
              />
              {errors.employee_contribution && <div className="invalid-feedback">{errors.employee_contribution}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                Employee Contribution Max <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                name="employee_contribution_max"
                className={`form-control form-control-sm ${errors.employee_contribution_max ? "is-invalid" : ""}`}
                value={formData.employee_contribution_max}
                onChange={handleChange}
                placeholder="Max amount"
                disabled={disabled}
              />
              {errors.employee_contribution_max && <div className="invalid-feedback">{errors.employee_contribution_max}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                Employer Contribution (In %) <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                name="employer_contribution"
                className={`form-control form-control-sm ${errors.employer_contribution ? "is-invalid" : ""}`}
                value={formData.employer_contribution}
                onChange={handleChange}
                placeholder="e.g. 0.75"
                disabled={disabled}
              />
              {errors.employer_contribution && <div className="invalid-feedback">{errors.employer_contribution}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                Employer Contribution Max <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                name="employer_contribution_max"
                className={`form-control form-control-sm ${errors.employer_contribution_max ? "is-invalid" : ""}`}
                value={formData.employer_contribution_max}
                onChange={handleChange}
                placeholder="Max amount"
                disabled={disabled}
              />
              {errors.employer_contribution_max && <div className="invalid-feedback">{errors.employer_contribution_max}</div>}
            </div>
          </>
        ) : (
          <>
            <div className="col-md-4 mb-3">
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                Employee Contribution
              </label>
              <input
                type="number"
                min="0"
                name="employee_contribution"
                className={`form-control form-control-sm ${errors.employee_contribution ? "is-invalid" : ""}`}
                value={formData.employee_contribution}
                onChange={handleChange}
                placeholder="Fixed amount"
                disabled={disabled}
              />
              {errors.employee_contribution && <div className="invalid-feedback">{errors.employee_contribution}</div>}
            </div>

            <div className="col-md-4 mb-3">
              <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
                Employer Contribution <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                min="0"
                name="employer_contribution"
                className={`form-control form-control-sm ${errors.employer_contribution ? "is-invalid" : ""}`}
                value={formData.employer_contribution}
                onChange={handleChange}
                placeholder="Fixed amount"
                disabled={disabled}
              />
              {errors.employer_contribution && <div className="invalid-feedback">{errors.employer_contribution}</div>}
            </div>
          </>
        )}

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Frequency <span className="text-danger">*</span>
          </label>
          <select className="form-select form-select-sm" name="frequency" value={formData.frequency} onChange={handleChange} disabled={disabled}>
            {FREQUENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {errors.frequency && <div className="invalid-feedback d-block">{errors.frequency}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Effective From
          </label>
          <input
            type="date"
            name="effective_from"
            className={`form-control form-control-sm ${errors.effective_from ? "is-invalid" : ""}`}
            value={formData.effective_from}
            onChange={handleChange}
            disabled={disabled}
          />
          {errors.effective_from && <div className="invalid-feedback">{errors.effective_from}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Effective To
          </label>
          <input
            type="date"
            name="effective_to"
            className={`form-control form-control-sm ${errors.effective_to ? "is-invalid" : ""}`}
            value={formData.effective_to}
            onChange={handleChange}
            min={formData.effective_from || undefined}
            disabled={disabled}
          />
          {errors.effective_to && <div className="invalid-feedback">{errors.effective_to}</div>}
        </div>

        <div className="col-md-4 mb-3">
          <label className="form-label" style={{ fontWeight: 600, fontSize: "12px" }}>
            Status
          </label>
          <select className="form-select form-select-sm" name="status" value={formData.status} onChange={handleChange} disabled={disabled}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!isEdit && saved && (
        <div className="d-flex justify-content-end align-items-center" style={{ paddingTop: "6px" }}>
          <span className="text-success" style={{ fontSize: "12px", fontWeight: 600 }}>
            Saved successfully
          </span>
        </div>
      )}
    </div>
  );
};

const AddEditLWFMaster = ({ refresh, existingField = null }) => {
  const { showLoader, hideLoader } = useLoader();
  const { hideModal } = useModal();
  const isEdit = Boolean(existingField);
  const excludeId = isEdit ? existingField.id : undefined;

  const [zones, setZones] = useState([]);
  const [rangesByState, setRangesByState] = useState({});
  const rangesByStateRef = useRef({});

  const rowCounterRef = useRef(0);
  const makeEmptyRow = () => ({
    key: ++rowCounterRef.current,
    formData: { ...INITIAL_FORM },
    errors: {},
    saved: false,
  });

  const [rows, setRows] = useState(() => (isEdit ? [] : [makeEmptyRow()]));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Errors stay hidden while the user is filling the form in and only appear once they've
  // clicked Submit/Update at least once; after that first attempt, edits re-validate live so
  // fixed fields clear immediately instead of requiring another submit click.
  const attemptedSubmitRef = useRef(false);
  const applyRows = useCallback(
    (nextRows) => (attemptedSubmitRef.current ? recomputeAllRows(nextRows, rangesByStateRef.current, excludeId) : nextRows),
    [excludeId]
  );

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const response = await getZones();
        const zoneData = response?.data?.data || response?.data || [];
        setZones(zoneData);
      } catch (error) {
        console.error("Error fetching zones:", error);
        toast.error("Failed to load states");
      }
    };
    fetchZones();
  }, []);

  const ensureRangesLoaded = useCallback(async (stateId, { force = false } = {}) => {
    if (!stateId) return;
    const key = String(stateId);
    const current = rangesByStateRef.current[key];
    if (!force && current && (current.loading || current.loaded)) return;

    const loadingState = { ...rangesByStateRef.current, [key]: { loading: true, loaded: false, ranges: current?.ranges || [] } };
    rangesByStateRef.current = loadingState;
    setRangesByState(loadingState);

    try {
      const response = await getLwfMasters(1, { filters: { state_id: stateId, status: 1 }, limit: 1000 });
      const body = response?.data;
      const listRows = body?.data?.data || body?.data || [];
      const ranges = listRows.map((r) => ({
        id: r.id,
        salary_from: Number(r.salary_from),
        salary_to: isBlank(r.salary_to) ? Infinity : Number(r.salary_to),
      }));

      const nextState = { ...rangesByStateRef.current, [key]: { loading: false, loaded: true, ranges } };
      rangesByStateRef.current = nextState;
      setRangesByState(nextState);
      if (attemptedSubmitRef.current) {
        setRows((prev) => recomputeAllRows(prev, nextState, excludeId));
      }
    } catch (error) {
      console.error("Error fetching existing LWF ranges:", error);
      const nextState = { ...rangesByStateRef.current, [key]: { loading: false, loaded: true, ranges: current?.ranges || [] } };
      rangesByStateRef.current = nextState;
      setRangesByState(nextState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludeId]);

  useEffect(() => {
    if (isEdit && existingField) {
      const formData = {
        state: existingField.state ?? existingField.state_id ?? "",
        salary_from: existingField.salary_from ?? "",
        salary_to: existingField.salary_to ?? "",
        amount_type: existingField.amount_type || "fixed",
        employee_contribution: existingField.employee_contribution ?? "",
        employee_contribution_max: existingField.employee_contribution_max ?? "",
        employer_contribution: existingField.employer_contribution ?? "",
        employer_contribution_max: existingField.employer_contribution_max ?? "",
        frequency: existingField.frequency ?? 1,
        effective_from: existingField.effective_from ? String(existingField.effective_from).slice(0, 10) : "",
        effective_to: existingField.effective_to ? String(existingField.effective_to).slice(0, 10) : "",
        status: existingField.status === undefined || existingField.status === null ? 1 : Number(existingField.status),
      };
      setRows([{ key: 1, formData, errors: {}, saved: false }]);
      if (formData.state) {
        ensureRangesLoaded(formData.state, { force: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existingField]);

  const handleFieldChange = (index, name, value) => {
    setRows((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, formData: { ...row.formData, [name]: value } } : row));
      return applyRows(next);
    });

    if (name === "state" && value) {
      ensureRangesLoaded(value);
    }
  };

  const handleAmountTypeChange = (index, value) => {
    setRows((prev) => {
      const next = prev.map((row, i) => {
        if (i !== index) return row;
        const resetMax = value === "fixed" ? { employee_contribution_max: "", employer_contribution_max: "" } : {};
        return { ...row, formData: { ...row.formData, amount_type: value, ...resetMax } };
      });
      return applyRows(next);
    });
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => applyRows(prev.filter((_, i) => i !== index)));
  };

  const handleSubmitAll = async () => {
    if (isSubmitting) return;

    attemptedSubmitRef.current = true;
    const validatedRows = recomputeAllRows(rows, rangesByStateRef.current, excludeId);
    const hasErrors = validatedRows.some((row) => !row.saved && Object.keys(row.errors).length > 0);

    if (hasErrors) {
      setRows(validatedRows);
      return;
    }

    setRows(validatedRows);
    setIsSubmitting(true);
    showLoader();

    try {
      let latestRows = validatedRows;
      let successMessage = "";
      let sawFailure = false;

      for (let index = 0; index < latestRows.length; index += 1) {
        const row = latestRows[index];
        if (row.saved) continue;

        const isPercentage = row.formData.amount_type === "percentage";
        const payload = buildPayload(row.formData, isPercentage);

        try {
          // eslint-disable-next-line no-await-in-loop
          const response = isEdit ? await updateLwfMaster(existingField.id, payload) : await createLwfMaster(payload);

          successMessage = response?.data?.message || successMessage;
          latestRows = latestRows.map((r, i) => (i === index ? { ...r, saved: true, errors: {} } : r));
          setRows(latestRows);

          if (!isEdit) {
            ensureRangesLoaded(row.formData.state, { force: true });
          }
        } catch (error) {
          console.error("Error saving LWF Master:", error);
          sawFailure = true;
          const respData = error?.response?.data;
          const serverFieldErrors = extractServerFieldErrors(respData);
          const message = respData?.message || "Failed to save LWF Master";
          const hasFieldErrors = Object.keys(serverFieldErrors).length > 0;

          latestRows = latestRows.map((r, i) => (i === index ? { ...r, errors: { ...r.errors, ...serverFieldErrors } } : r));
          setRows(latestRows);
          if (!hasFieldErrors) {
            // Business/overlap validation errors (e.g. salary range overlap) have no single
            // field to attach to, so they're surfaced via toast only - never rendered inline.
            toast.error(message);
          }
        }
      }

      if (!sawFailure) {
        toast.success(successMessage || (isEdit ? "LWF Master updated successfully!" : "LWF Master added successfully!"));
        refresh?.();
        hideModal?.();
      }
    } finally {
      setIsSubmitting(false);
      hideLoader();
    }
  };

  return (
    <div style={{ marginTop: "10px" }}>
      <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "4px" }}>
        {rows.map((row, index) => (
          <LwfRowForm
            key={row.key}
            row={row}
            index={index}
            zones={zones}
            isEdit={isEdit}
            canRemove={rows.length > 1}
            isSubmitting={isSubmitting}
            onFieldChange={handleFieldChange}
            onAmountTypeChange={handleAmountTypeChange}
            onRemove={handleRemoveRow}
          />
        ))}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "12px",
          borderTop: "1px solid #e0e0e0",
        }}
      >
        
        <div>
          {!isEdit && (
            <button
              type="button"
              onClick={handleAddRow}
              disabled={isSubmitting}
              style={{
                backgroundColor: "#f0f0f0",
                border: "1px dashed #999",
                padding: "5px 10px",
                borderRadius: "4px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "#333",
              }}
            >
              <AddCircleOutline fontSize="small" />
              Add Row
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button type="button" onClick={hideModal} className="btn btn-secondary btn-sm" disabled={isSubmitting}>
            {isEdit ? "Cancel" : "Close"}
          </button>
          <button type="button" className="btn btn-success btn-sm" disabled={isSubmitting} onClick={handleSubmitAll}>
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditLWFMaster;